import type { Mode } from "../types/story";

/**
 * Session resolution from the URL.
 *
 * URL shape: /play?family=<id>&child=<name>[&story=<storyId>&mode=interactive]
 *
 *   - family + child are always required (resolveBase). child is the only PII.
 *   - No `story` → the library shelf is shown (loadCatalog + App); the child
 *     picks a story on demand, always interactive.
 *   - `story` present → that single story is opened directly (resolveSession),
 *     the study/test override and the only path into passive mode. `story` and
 *     `mode` come from the URL only; there is no server-side or file-based
 *     assignment — the library shelf replaces it.
 */

export interface SessionConfig {
  family: string;
  child: string;
  storyId: string;
  mode: Mode;
}

/** Who is reading tonight — resolved before any story is chosen (the library). */
export interface SessionBase {
  family: string;
  child: string;
}

export interface ResolveResult {
  config?: SessionConfig;
  error?: string;
}

export interface BaseResult {
  base?: SessionBase;
  error?: string;
}

function normalizeMode(raw: string | null | undefined): Mode | undefined {
  if (raw === "interactive" || raw === "passive") return raw;
  return undefined;
}

export function parseQuery(search: string): URLSearchParams {
  return new URLSearchParams(search);
}

/** Resolve who is reading (family + child) — always required, before any story. */
export function resolveBase(search: string): BaseResult {
  const params = parseQuery(search);
  const family = (params.get("family") ?? "").trim();
  const child = (params.get("child") ?? "").trim();
  if (!family) return { error: "Missing ?family in the link." };
  if (!child) return { error: "Missing ?child in the link." };
  return { base: { family, child } };
}

/** Did the URL deep-link a single story (which bypasses the library shelf)? */
export function hasStoryParam(search: string): boolean {
  return (parseQuery(search).get("story") ?? "").trim().length > 0;
}

/**
 * Resolve a single-story deep-link from the URL (the study/test override that
 * bypasses the library). `story` is required; `mode` defaults to interactive
 * when omitted (pass `?mode=passive` for the passive arm). The URL is the only
 * source.
 */
export function resolveSession(search: string): ResolveResult {
  const { base, error } = resolveBase(search);
  if (!base) return { error };

  const params = parseQuery(search);
  const storyId = (params.get("story") ?? "").trim();
  if (!storyId) {
    return {
      error: "No ?story in the link — use the library, or add ?story=<storyId>.",
    };
  }
  const mode = normalizeMode(params.get("mode")) ?? "interactive";

  return { config: { family: base.family, child: base.child, storyId, mode } };
}
